import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Play, Vote, DollarSign, TrendingUp, Users, BarChart3 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { SectionProgressBar } from "@/components/SectionProgressBar";
import { ScrollToTopButton } from "@/components/ScrollToTopButton";
import { LazySection } from "@/components/LazySection";
import { OptimizedImage } from "@/components/OptimizedImage";
import {
  LiveStreamPreviewWithSuspense,
  ScrollEngagementPromptWithSuspense,
  ExitIntentModalWithSuspense,
  OnboardingTourWithSuspense,
} from "./Index.lazy";
import { analytics } from "@/lib/analytics";
import { calculateSizes } from "@/utils/imageOptimization";
import logo from "@/assets/logo.png";

const Index = () => {
  useEffect(() => {
    // Track page view
    analytics.pageView('/');
    
    // Track time on page
    const startTime = Date.now();
    
    // Track lazy loading performance
    if ('PerformanceObserver' in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'largest-contentful-paint') {
            analytics.userEngagement('lcp_measurement', {
              value: entry.startTime,
            });
          }
        }
      });
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    }
    
    return () => {
      const timeSpent = Date.now() - startTime;
      analytics.timeOnPage(timeSpent);
    };
  }, []);

  const handleCtaClick = (ctaName: string, location: string) => {
    analytics.ctaClick(ctaName, location);
  };

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "HiStage",
        "url": "https://histage.lovable.app",
        "logo": "https://histage.lovable.app/logo.png",
        "description": "Fan-driven live streaming platform connecting creators and audiences through interactive voting, tipping, and real-time engagement",
        "sameAs": [
          "https://twitter.com/HiStage",
          "https://facebook.com/HiStage",
          "https://instagram.com/HiStage"
        ]
      },
      {
        "@type": "WebSite",
        "name": "HiStage",
        "url": "https://histage.lovable.app",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://histage.lovable.app/discover?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "WebApplication",
        "name": "HiStage",
        "operatingSystem": "Any",
        "applicationCategory": "EntertainmentApplication",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      }
    ]
  };

  return (
    <>
      <SectionProgressBar />
      <ExitIntentModalWithSuspense />
      <OnboardingTourWithSuspense />
      <ScrollEngagementPromptWithSuspense />
      <ScrollToTopButton />
      <SEO
        title="HiStage - Fan-Driven Live Streaming Platform"
        description="Watch live performances and engage with creators through interactive voting, tipping, and real-time chat. Join thousands of fans and creators on HiStage - the ultimate fan-driven live stage for music, comedy, gaming, and more."
        image="/assets/hero-stage.jpg"
        url="/"
        keywords={[
          "live streaming platform",
          "fan engagement",
          "creator platform",
          "interactive live shows",
          "live tipping",
          "fan voting",
          "live performances",
          "music streaming",
          "comedy shows",
          "gaming streams",
          "creator earnings",
          "audience interaction"
        ]}
        schema={schema}
      />
      <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50" role="navigation" aria-label="Main navigation">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src={logo} 
                alt="HiStage Logo" 
                className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto brightness-110"
                width="200"
                height="64"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <a href="#discover" className="text-sm xl:text-base text-foreground hover:text-primary transition-colors">Discover</a>
              <a href="#how-it-works" className="text-sm xl:text-base text-foreground hover:text-primary transition-colors">How it works</a>
              <a href="#blog" className="text-sm xl:text-base text-foreground hover:text-primary transition-colors">Blog</a>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              <Link to="/auth">
                <Button variant="ghost" size="sm" className="text-sm md:text-base">Log In</Button>
              </Link>
              <Link to="/auth">
                <Button size="sm" className="rounded-full px-4 sm:px-6 text-sm md:text-base">Sign Up</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main>
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-b from-bg-50 to-white" aria-labelledby="hero-heading">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div className="space-y-4 sm:space-y-6 text-center lg:text-left">
              <h1 id="hero-heading" className="font-bold text-foreground leading-tight">
                The Fan-Driven<br />Live Stage
              </h1>
              <p className="text-muted-foreground leading-relaxed max-w-xl mx-auto lg:mx-0">
                Watch your favorite artists perform live and engage by voting, tipping, and unlocking the show.
              </p>
              <Link to="/auth" onClick={() => handleCtaClick('Get Started', 'hero')}>
                <Button size="lg" className="rounded-full px-6 sm:px-8 md:px-10">
                  Get Started
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-lg gradient-purple p-1">
                <div className="relative rounded-3xl overflow-hidden">
                  <OptimizedImage
                    src="/assets/hero-stage.jpg"
                    webpSrc="/assets/hero-stage.webp"
                    avifSrc="/assets/hero-stage.avif"
                    srcSet="/assets/hero-stage-640w.jpg 640w, /assets/hero-stage-800w.jpg 800w, /assets/hero-stage-1200w.jpg 1200w, /assets/hero-stage.jpg 1920w"
                    webpSrcSet="/assets/hero-stage-640w.webp 640w, /assets/hero-stage-800w.webp 800w, /assets/hero-stage-1200w.webp 1200w, /assets/hero-stage.webp 1920w"
                    avifSrcSet="/assets/hero-stage-640w.avif 640w, /assets/hero-stage-800w.avif 800w, /assets/hero-stage-1200w.avif 1200w, /assets/hero-stage.avif 1920w"
                    sizes={calculateSizes({
                      mobile: '100vw',
                      tablet: '50vw',
                      default: '50vw'
                    })}
                    alt="Musician performing live on HiStage with interactive fan engagement and voting features"
                    className="w-full aspect-video object-cover"
                    priority={true}
                    width={1200}
                    height={675}
                    aspectRatio="16/9"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/40 to-purple-800/60" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button 
                      className="h-20 w-20 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform cursor-pointer shadow-xl"
                      aria-label="Play video demo"
                    >
                      <Play className="h-10 w-10 text-primary fill-primary ml-1" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Stream Preview - Lazy Loaded */}
      <LazySection rootMargin="200px">
        <LiveStreamPreviewWithSuspense />
      </LazySection>

      {/* Live Performance Section */}
      <LazySection rootMargin="100px">
      <section className="py-12 sm:py-16 md:py-20" id="discover" aria-labelledby="discover-heading">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-12 items-start">
            {/* Vote Section */}
            <article className="clean-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
              <h2 id="discover-heading" className="font-bold text-foreground">Live Performance</h2>
              <div className="space-y-3 sm:space-y-4">
                <h3 className="font-semibold text-foreground">Vote for the next song</h3>
                <div className="space-y-2 sm:space-y-3" role="radiogroup" aria-label="Song selection">
                  {["Song A", "Song B", "Song C"].map((song) => (
                    <label key={song} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 min-h-[44px] rounded-lg sm:rounded-xl bg-muted/50 hover:bg-muted cursor-pointer transition-colors">
                      <input type="radio" name="song" className="h-4 w-4 sm:h-5 sm:w-5 text-primary" aria-label={`Vote for ${song}`} />
                      <span className="text-sm sm:text-base text-foreground">{song}</span>
                    </label>
                  ))}
                </div>
                <Button className="w-full rounded-full py-5 sm:py-6 text-base sm:text-lg min-h-[44px]">Vote</Button>
              </div>

              {/* Tipping Section */}
              <div className="pt-4 sm:pt-6 border-t space-y-3 sm:space-y-4">
                <h3 className="font-bold text-foreground">Tipping & Unlocks</h3>
                <div className="space-y-2 sm:space-y-3">
                  <label htmlFor="tip-amount" className="flex items-center justify-between">
                    <span className="text-sm sm:text-base text-foreground">Send a tip</span>
                    <span className="text-xl sm:text-2xl font-bold text-foreground">100</span>
                  </label>
                  <input 
                    id="tip-amount"
                    type="range" 
                    min="0" 
                    max="1000" 
                    defaultValue="100" 
                    aria-label="Tip amount in StagCoins"
                    className="w-full h-3 bg-primary rounded-full appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:border-primary" 
                  />
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-muted-foreground">Backstage Q&A</span>
                      <span className="text-foreground font-semibold">500 StagCos</span>
                    </div>
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-muted-foreground">Unreleased Songs</span>
                      <span className="text-foreground font-semibold">1000 StagCos</span>
                    </div>
                  </div>
                  <Button className="w-full rounded-full py-5 sm:py-6 text-base sm:text-lg">Send Tip</Button>
                </div>
              </div>
            </article>

            {/* Live Stream + Dashboard */}
            <div className="space-y-4 sm:space-y-6">
              <div className="clean-card rounded-2xl sm:rounded-3xl overflow-hidden">
                <div className="relative">
                  <OptimizedImage 
                    src="/assets/hero-stage-small.jpg"
                    webpSrc="/assets/hero-stage-small.webp"
                    avifSrc="/assets/hero-stage-small.avif"
                    srcSet="/assets/hero-stage-small.jpg 800w"
                    webpSrcSet="/assets/hero-stage-small.webp 800w"
                    avifSrcSet="/assets/hero-stage-small.avif 800w"
                    sizes={calculateSizes({
                      mobile: '100vw',
                      tablet: '50vw',
                      default: '50vw'
                    })}
                    alt="Live streaming interface with real-time viewer count and interactive features"
                    className="w-full aspect-video object-cover"
                    width={800}
                    height={450}
                    aspectRatio="16/9"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/40 to-purple-800/60" />
                  <div className="absolute top-2 left-2 sm:top-4 sm:left-4">
                    <span className="px-2 sm:px-4 py-1 sm:py-1.5 bg-red-500 text-white text-xs sm:text-sm font-bold rounded-md sm:rounded-lg">LIVE</span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button 
                      className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-white/90 flex items-center justify-center min-h-[44px] min-w-[44px]"
                      aria-label="Play live stream preview"
                    >
                      <Play className="h-6 w-6 sm:h-8 sm:w-8 text-primary fill-primary ml-1" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Creator Dashboard */}
              <div className="clean-card rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-foreground">Creator Dashboard</h3>
                  <button className="text-xs sm:text-sm text-muted-foreground hover:text-foreground min-h-[44px] px-2 sm:px-3" aria-label="View full dashboard">Unlocal</button>
                </div>
                
                <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                  <div className="space-y-1.5 sm:space-y-2">
                    <h4 className="text-xs sm:text-sm font-medium text-muted-foreground">Performance Details</h4>
                    <div className="space-y-0.5 sm:space-y-1">
                      <p className="text-sm sm:text-base md:text-lg font-semibold text-foreground">Song A</p>
                      <p className="text-xs sm:text-sm text-muted-foreground">74,395</p>
                      <p className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">$123:45</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <h4 className="text-xs sm:text-sm font-medium text-muted-foreground">Real-Time Analytics</h4>
                    <div className="h-16 sm:h-20 md:h-24 flex items-end justify-between gap-0.5 sm:gap-1">
                      {[40, 65, 45, 80, 60, 90, 70, 85, 55, 75].map((height, i) => (
                        <div key={i} className="flex-1 bg-primary/20 rounded-t" style={{ height: `${height}%` }}>
                          <div className="h-full bg-primary rounded-t" style={{ height: '70%' }} />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>10:00</span>
                      <span>10:45</span>
                      <span>10:45</span>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Earnings</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </LazySection>

      {/* Features Grid - Lazy Loaded */}
      <LazySection rootMargin="150px">
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-bg-50" id="how-it-works" aria-labelledby="features-heading">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 id="features-heading" className="font-bold text-foreground mb-3 sm:mb-4">
              What Makes HiStage Different
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto px-4">
              Interactive features that put fans in control
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {[
              {
                icon: Vote,
                title: "Fan Voting",
                description: "Decide what happens next in real-time performances"
              },
              {
                icon: DollarSign,
                title: "Smart Tipping",
                description: "Unlock exclusive content and special moments"
              },
              {
                icon: TrendingUp,
                title: "Leaderboards",
                description: "Climb the ranks and get recognized as a top fan"
              },
              {
                icon: Users,
                title: "Live Engagement",
                description: "Chat and interact with creators and other fans"
              },
              {
                icon: BarChart3,
                title: "Creator Analytics",
                description: "Track performance and earnings in real-time"
              },
              {
                icon: Play,
                title: "HD Streaming",
                description: "Crystal clear video quality for immersive experiences"
              }
            ].map((feature, i) => (
              <div 
                key={i} 
                className="clean-card rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 hover-lift cursor-pointer min-h-[140px] sm:min-h-[160px] touch-manipulation"
                role="article"
                aria-labelledby={`feature-${i}-title`}
              >
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg sm:rounded-xl bg-primary/10 flex items-center justify-center mb-3 sm:mb-4" aria-hidden="true">
                  <feature.icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <h3 id={`feature-${i}-title`} className="font-bold text-foreground mb-1.5 sm:mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </LazySection>

      {/* CTA Section - Lazy Loaded */}
      <LazySection rootMargin="100px">
      <section className="py-12 sm:py-16 md:py-20" aria-labelledby="cta-heading">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="clean-card rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 text-center max-w-3xl mx-auto">
            <h2 id="cta-heading" className="font-bold text-foreground mb-3 sm:mb-4">
              Ready to Take the Stage?
            </h2>
            <p className="text-muted-foreground mb-6 sm:mb-8 px-4">
              Join thousands of creators and fans already using HiStage
            </p>
            <Link to="/auth" onClick={() => handleCtaClick('Get Started Free', 'cta-section')}>
              <Button size="lg" className="rounded-full px-8 sm:px-10 md:px-12">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </section>
      </LazySection>

      </main>
      
      {/* Footer - Lazy Loaded */}
      <LazySection rootMargin="50px">
      <footer className="border-t py-8 sm:py-10 md:py-12 bg-white" role="contentinfo" aria-label="Footer">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-3 sm:mb-4">
                <img 
                  src={logo} 
                  alt="HiStage Logo" 
                  className="h-8 sm:h-10 md:h-12 w-auto brightness-110"
                  width="150"
                  height="48"
                />
              </div>
              <p className="text-xs sm:text-sm text-foreground">
                Fan-powered. Creator-owned.
              </p>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-foreground mb-2 sm:mb-3">Product</h3>
              <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-foreground">
                <div className="hover:text-primary cursor-pointer">Features</div>
                <div className="hover:text-primary cursor-pointer">Pricing</div>
                <div className="hover:text-primary cursor-pointer">Analytics</div>
              </div>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-foreground mb-2 sm:mb-3">Company</h3>
              <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-foreground">
                <div className="hover:text-primary cursor-pointer">About</div>
                <div className="hover:text-primary cursor-pointer">Blog</div>
                <div className="hover:text-primary cursor-pointer">Careers</div>
              </div>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-semibold text-foreground mb-2 sm:mb-3">Support</h3>
              <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-foreground">
                <div className="hover:text-primary cursor-pointer">Help Center</div>
                <div className="hover:text-primary cursor-pointer">Contact</div>
                <Link to="/terms" className="block hover:text-primary cursor-pointer">Terms</Link>
                <Link to="/privacy" className="block hover:text-primary cursor-pointer">Privacy</Link>
                <Link to="/csae-policy" className="block hover:text-primary cursor-pointer">Child Safety</Link>
              </div>
            </div>
          </div>
          <div className="pt-6 sm:pt-8 border-t text-center text-xs sm:text-sm text-muted-foreground">
            © 2025 HiStage. All rights reserved.
          </div>
        </div>
      </footer>
      </LazySection>
      </div>
    </>
  );
};

export default Index;
